import apiClient from './client';

export type ShowroomApprovalStatus = 'draft' | 'pending' | 'approved' | 'rejected';
export type ShowroomInventoryType = 'new' | 'used' | 'both';
export type ShowroomCarStatus =
  | 'draft'
  | 'pending'
  | 'published'
  | 'unpublished'
  | 'reserved'
  | 'sold'
  | 'rejected';
export type ShowroomOrderStatus =
  | 'draft'
  | 'confirmed'
  | 'paid'
  | 'delivered'
  | 'cancelled'
  | 'refunded';
export type ShowroomLeadStatus = 'new' | 'contacted' | 'negotiation' | 'won' | 'lost';

export interface ShowroomProfile {
  id: number;
  store: number;
  store_name?: string;
  vendor_id?: number;
  company_name: string;
  mobile_number: string;
  email?: string;
  country_code: string;
  city?: string;
  location?: string;
  latitude?: string | number | null;
  longitude?: string | number | null;
  trade_license?: string;
  logo?: string;
  description?: string;
  inventory_type: ShowroomInventoryType;
  onboarding_status: ShowroomApprovalStatus;
  submitted_at?: string | null;
  approved_at?: string | null;
  rejected_at?: string | null;
  rejection_reason?: string;
  reservation_window_hours?: number;
  working_hours?: Record<string, unknown>;
  payment_methods?: unknown[];
  discount_rules?: Record<string, unknown>;
  notification_settings?: Record<string, unknown>;
  commission_percent?: string | number;
  subscription_plan?: string;
  is_active?: boolean;
}

export interface ShowroomBranch {
  id: number;
  showroom: number;
  name: string;
  code?: string;
  country_code?: string;
  city?: string;
  location?: string;
  latitude?: string | number | null;
  longitude?: string | number | null;
  manager_name?: string;
  manager_phone?: string;
  is_active?: boolean;
}

export interface ShowroomTeamMember {
  id: number;
  showroom: number;
  user?: number | null;
  full_name: string;
  email?: string;
  phone?: string;
  role: 'owner' | 'manager' | 'sales_staff';
  module_permissions?: Record<string, boolean>;
  is_active?: boolean;
}

export interface ShowroomCarImage {
  id: number;
  image: string;
  is_primary?: boolean;
  sort_order?: number;
}

export interface ShowroomCar {
  id: number;
  showroom: number;
  showroom_name?: string;
  branch?: number | null;
  branch_name?: string;
  brand: string;
  model: string;
  variant?: string;
  year: number;
  price: string | number;
  cost_price?: string | number;
  mileage?: number | null;
  condition: 'new' | 'used';
  vin?: string;
  plate_number?: string;
  engine?: string;
  fuel?: string;
  transmission?: string;
  color?: string;
  description?: string;
  features?: string[];
  status: ShowroomCarStatus;
  is_active?: boolean;
  is_published?: boolean;
  reserved_until?: string | null;
  sold_at?: string | null;
  published_at?: string | null;
  rejection_reason?: string;
  admin_note?: string;
  country_code?: string;
  city?: string;
  commission_percent?: string | number;
  ai_description?: string;
  ai_price_suggestion?: string | number | null;
  ai_condition_summary?: string;
  images?: ShowroomCarImage[];
  created_at?: string;
  updated_at?: string;
}

export interface ShowroomOrder {
  id: number;
  number: string;
  invoice_number?: string;
  showroom: number;
  branch?: number | null;
  car: number;
  car_title?: string;
  lead?: number | null;
  customer_name: string;
  customer_phone?: string;
  customer_email?: string;
  status: ShowroomOrderStatus;
  payment_method: 'cash' | 'bank' | 'online' | 'card';
  reservation_expires_at?: string | null;
  subtotal?: string | number;
  discount_amount?: string | number;
  tax_amount?: string | number;
  total_amount?: string | number;
  paid_amount?: string | number;
  outstanding_amount?: string | number;
  cost_snapshot?: string | number;
  commission_percent?: string | number;
  commission_amount?: string | number;
  profit_amount?: string | number;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface ShowroomLead {
  id: number;
  showroom: number;
  branch?: number | null;
  car?: number | null;
  car_title?: string;
  customer_name: string;
  customer_phone?: string;
  customer_email?: string;
  source?: string;
  status: ShowroomLeadStatus;
  message?: string;
  follow_up_notes?: string;
  next_follow_up_at?: string | null;
  lead_score?: number;
  assigned_to?: number | null;
  created_at?: string;
  updated_at?: string;
}

export interface ShowroomExpense {
  id: number;
  showroom: number;
  branch?: number | null;
  car?: number | null;
  category?: string;
  amount: string | number;
  occurred_on: string;
  note?: string;
  created_at?: string;
}

export interface ShowroomTestDrive {
  id: number;
  showroom: number;
  car: number;
  customer_name: string;
  customer_phone?: string;
  requested_at?: string;
  scheduled_for?: string | null;
  assigned_staff?: number | null;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
  notes?: string;
}

export interface ShowroomTradeIn {
  id: number;
  showroom: number;
  target_car?: number | null;
  linked_order?: number | null;
  customer_name: string;
  customer_phone?: string;
  old_car_brand?: string;
  old_car_model?: string;
  old_car_year?: number | null;
  old_car_mileage?: number | null;
  old_car_condition?: string;
  old_car_description?: string;
  offered_amount?: string | number;
  status: 'submitted' | 'reviewing' | 'offered' | 'accepted' | 'rejected';
  notes?: string;
  created_at?: string;
}

export interface ShowroomDocument {
  id: number;
  showroom: number;
  car?: number | null;
  order?: number | null;
  doc_type: 'ownership' | 'insurance' | 'service_history' | 'license' | 'other';
  title?: string;
  file: string;
  is_private?: boolean;
  created_at?: string;
}

export interface ShowroomDashboardKpis {
  total_cars: number;
  draft_cars: number;
  published_cars: number;
  sold_cars: number;
  reserved_cars: number;
  revenue: string;
  pending_orders: number;
  monthly_sales: string;
  leads_count: number;
  profit_summary: string;
  commission_summary: string;
  expenses_total: string;
  net_profit: string;
  outstanding_payments: string;
}

function extractRows<T>(payload: any): T[] {
  if (Array.isArray(payload)) return payload as T[];
  if (payload && Array.isArray(payload.results)) return payload.results as T[];
  return [];
}

function sanitizePatch<T extends Record<string, unknown>>(payload: T) {
  const clean: Record<string, unknown> = {};
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined) clean[key] = value;
  });
  return clean;
}

export const showroomApi = {
  requestOtp: async (mobile_number: string) => {
    const { data } = await apiClient.post(
      '/showroom/request-otp/',
      { mobile_number },
      { skipAuth: true } as any,
    );
    return data;
  },

  verifyOtp: async (mobile_number: string, otp_code: string) => {
    const { data } = await apiClient.post(
      '/showroom/verify-otp/',
      { mobile_number, otp_code },
      { skipAuth: true } as any,
    );
    return data;
  },

  register: async (payload: FormData) => {
    const { data } = await apiClient.post('/showroom/register/', payload, {
      headers: { 'Content-Type': 'multipart/form-data' },
      skipAuth: true,
    } as any);
    return data;
  },

  getDashboard: async (country_code?: string) => {
    const { data } = await apiClient.get<ShowroomDashboardKpis>('/showroom/dashboard/', {
      params: country_code ? { country_code } : undefined,
    });
    return data;
  },

  getProfiles: async () => {
    const { data } = await apiClient.get('/showroom/profiles/');
    return extractRows<ShowroomProfile>(data);
  },

  getProfileById: async (id: number) => {
    const { data } = await apiClient.get<ShowroomProfile>(`/showroom/profiles/${id}/`);
    return data;
  },

  updateProfile: async (
    id: number,
    payload: Partial<ShowroomProfile> | FormData,
  ) => {
    const isFormData = payload instanceof FormData;
    const { data } = await apiClient.patch<ShowroomProfile>(
      `/showroom/profiles/${id}/`,
      isFormData ? payload : sanitizePatch(payload),
      isFormData
        ? { headers: { 'Content-Type': 'multipart/form-data' } }
        : undefined,
    );
    return data;
  },

  submitProfile: async (id: number) => {
    const { data } = await apiClient.post(`/showroom/profiles/${id}/submit/`);
    return data;
  },

  approveProfile: async (id: number) => {
    const { data } = await apiClient.post(`/showroom/profiles/${id}/approve/`);
    return data;
  },

  rejectProfile: async (id: number, reason = '') => {
    const { data } = await apiClient.post(`/showroom/profiles/${id}/reject/`, { reason });
    return data;
  },

  setCommission: async (id: number, commission_percent: number) => {
    const { data } = await apiClient.post(`/showroom/profiles/${id}/set-commission/`, {
      commission_percent,
    });
    return data;
  },

  getBranches: async (params?: Record<string, unknown>) => {
    const { data } = await apiClient.get('/showroom/branches/', { params });
    return extractRows<ShowroomBranch>(data);
  },

  createBranch: async (payload: Partial<ShowroomBranch>) => {
    const { data } = await apiClient.post<ShowroomBranch>('/showroom/branches/', payload);
    return data;
  },

  updateBranch: async (id: number, payload: Partial<ShowroomBranch>) => {
    const { data } = await apiClient.patch<ShowroomBranch>(
      `/showroom/branches/${id}/`,
      sanitizePatch(payload),
    );
    return data;
  },

  deleteBranch: async (id: number) => {
    await apiClient.delete(`/showroom/branches/${id}/`);
  },

  getTeam: async (params?: Record<string, unknown>) => {
    const { data } = await apiClient.get('/showroom/team/', { params });
    return extractRows<ShowroomTeamMember>(data);
  },

  createTeamMember: async (payload: Partial<ShowroomTeamMember>) => {
    const { data } = await apiClient.post<ShowroomTeamMember>('/showroom/team/', payload);
    return data;
  },

  updateTeamMember: async (id: number, payload: Partial<ShowroomTeamMember>) => {
    const { data } = await apiClient.patch<ShowroomTeamMember>(
      `/showroom/team/${id}/`,
      sanitizePatch(payload),
    );
    return data;
  },

  deleteTeamMember: async (id: number) => {
    await apiClient.delete(`/showroom/team/${id}/`);
  },

  getCars: async (params?: Record<string, unknown>) => {
    const { data } = await apiClient.get('/showroom/cars/', { params });
    return extractRows<ShowroomCar>(data);
  },

  getCarById: async (id: number) => {
    const { data } = await apiClient.get<ShowroomCar>(`/showroom/cars/${id}/`);
    return data;
  },

  createCar: async (payload: Partial<ShowroomCar>) => {
    const { data } = await apiClient.post<ShowroomCar>('/showroom/cars/', payload);
    return data;
  },

  updateCar: async (id: number, payload: Partial<ShowroomCar>) => {
    const { data } = await apiClient.patch<ShowroomCar>(
      `/showroom/cars/${id}/`,
      sanitizePatch(payload),
    );
    return data;
  },

  deleteCar: async (id: number) => {
    await apiClient.delete(`/showroom/cars/${id}/`);
  },

  uploadCarImage: async (
    carId: number,
    image: File,
    options?: { isPrimary?: boolean; sortOrder?: number },
  ) => {
    const body = new FormData();
    body.append('image', image);
    if (options?.isPrimary) body.append('is_primary', 'true');
    if (typeof options?.sortOrder === 'number') {
      body.append('sort_order', String(options.sortOrder));
    }
    const { data } = await apiClient.post<ShowroomCarImage>(
      `/showroom/cars/${carId}/upload-image/`,
      body,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },

  carAction: async (
    carId: number,
    action:
      | 'save_draft'
      | 'publish'
      | 'unpublish'
      | 'mark_reserved'
      | 'mark_sold'
      | 'reject',
    payload?: Record<string, unknown>,
  ) => {
    const { data } = await apiClient.post(`/showroom/cars/${carId}/${action}/`, payload || {});
    return data;
  },

  aiSuggestPrice: async (carId: number) => {
    const { data } = await apiClient.post(`/showroom/cars/${carId}/ai-suggest-price/`);
    return data;
  },

  aiAutoDescription: async (carId: number) => {
    const { data } = await apiClient.post(`/showroom/cars/${carId}/ai-auto-description/`);
    return data;
  },

  getOrders: async (params?: Record<string, unknown>) => {
    const { data } = await apiClient.get('/showroom/orders/', { params });
    return extractRows<ShowroomOrder>(data);
  },

  getOrderById: async (id: number) => {
    const { data } = await apiClient.get<ShowroomOrder>(`/showroom/orders/${id}/`);
    return data;
  },

  createOrder: async (payload: Partial<ShowroomOrder>) => {
    const { data } = await apiClient.post<ShowroomOrder>('/showroom/orders/', payload);
    return data;
  },

  updateOrder: async (id: number, payload: Partial<ShowroomOrder>) => {
    const { data } = await apiClient.patch<ShowroomOrder>(
      `/showroom/orders/${id}/`,
      sanitizePatch(payload),
    );
    return data;
  },

  orderAction: async (
    orderId: number,
    action: 'confirm' | 'cancel' | 'deliver' | 'add-payment' | 'refund',
    payload?: Record<string, unknown>,
  ) => {
    const { data } = await apiClient.post(`/showroom/orders/${orderId}/${action}/`, payload || {});
    return data;
  },

  createPosSale: async (payload: Record<string, unknown>) => {
    const { data } = await apiClient.post('/showroom/orders/pos-sale/', payload);
    return data;
  },

  releaseExpiredReservations: async () => {
    const { data } = await apiClient.post('/showroom/orders/release-expired/');
    return data;
  },

  getOrderInvoice: async (orderId: number) => {
    const { data } = await apiClient.get(`/showroom/orders/${orderId}/invoice/`);
    return data;
  },

  getLeads: async (params?: Record<string, unknown>) => {
    const { data } = await apiClient.get('/showroom/leads/', { params });
    return extractRows<ShowroomLead>(data);
  },

  createLead: async (payload: Partial<ShowroomLead>) => {
    const { data } = await apiClient.post<ShowroomLead>('/showroom/leads/', payload);
    return data;
  },

  updateLead: async (id: number, payload: Partial<ShowroomLead>) => {
    const { data } = await apiClient.patch<ShowroomLead>(
      `/showroom/leads/${id}/`,
      sanitizePatch(payload),
    );
    return data;
  },

  deleteLead: async (id: number) => {
    await apiClient.delete(`/showroom/leads/${id}/`);
  },

  getExpenses: async (params?: Record<string, unknown>) => {
    const { data } = await apiClient.get('/showroom/expenses/', { params });
    return extractRows<ShowroomExpense>(data);
  },

  createExpense: async (payload: Partial<ShowroomExpense>) => {
    const { data } = await apiClient.post<ShowroomExpense>('/showroom/expenses/', payload);
    return data;
  },

  updateExpense: async (id: number, payload: Partial<ShowroomExpense>) => {
    const { data } = await apiClient.patch<ShowroomExpense>(
      `/showroom/expenses/${id}/`,
      sanitizePatch(payload),
    );
    return data;
  },

  deleteExpense: async (id: number) => {
    await apiClient.delete(`/showroom/expenses/${id}/`);
  },

  getTestDrives: async (params?: Record<string, unknown>) => {
    const { data } = await apiClient.get('/showroom/test-drives/', { params });
    return extractRows<ShowroomTestDrive>(data);
  },

  createTestDrive: async (payload: Partial<ShowroomTestDrive>) => {
    const { data } = await apiClient.post<ShowroomTestDrive>('/showroom/test-drives/', payload);
    return data;
  },

  updateTestDrive: async (id: number, payload: Partial<ShowroomTestDrive>) => {
    const { data } = await apiClient.patch<ShowroomTestDrive>(
      `/showroom/test-drives/${id}/`,
      sanitizePatch(payload),
    );
    return data;
  },

  deleteTestDrive: async (id: number) => {
    await apiClient.delete(`/showroom/test-drives/${id}/`);
  },

  getTradeIns: async (params?: Record<string, unknown>) => {
    const { data } = await apiClient.get('/showroom/trade-ins/', { params });
    return extractRows<ShowroomTradeIn>(data);
  },

  createTradeIn: async (payload: Partial<ShowroomTradeIn>) => {
    const { data } = await apiClient.post<ShowroomTradeIn>('/showroom/trade-ins/', payload);
    return data;
  },

  updateTradeIn: async (id: number, payload: Partial<ShowroomTradeIn>) => {
    const { data } = await apiClient.patch<ShowroomTradeIn>(
      `/showroom/trade-ins/${id}/`,
      sanitizePatch(payload),
    );
    return data;
  },

  deleteTradeIn: async (id: number) => {
    await apiClient.delete(`/showroom/trade-ins/${id}/`);
  },

  getDocuments: async (params?: Record<string, unknown>) => {
    const { data } = await apiClient.get('/showroom/documents/', { params });
    return extractRows<ShowroomDocument>(data);
  },

  createDocument: async (payload: FormData | Partial<ShowroomDocument>) => {
    const isFormData = payload instanceof FormData;
    const { data } = await apiClient.post<ShowroomDocument>(
      '/showroom/documents/',
      payload,
      isFormData
        ? { headers: { 'Content-Type': 'multipart/form-data' } }
        : undefined,
    );
    return data;
  },

  updateDocument: async (id: number, payload: FormData | Partial<ShowroomDocument>) => {
    const isFormData = payload instanceof FormData;
    const { data } = await apiClient.patch<ShowroomDocument>(
      `/showroom/documents/${id}/`,
      isFormData ? payload : sanitizePatch(payload),
      isFormData
        ? { headers: { 'Content-Type': 'multipart/form-data' } }
        : undefined,
    );
    return data;
  },

  deleteDocument: async (id: number) => {
    await apiClient.delete(`/showroom/documents/${id}/`);
  },

  getSalesReport: async (params?: Record<string, unknown>) => {
    const { data } = await apiClient.get('/showroom/reports/sales/', { params });
    return data;
  },

  getProfitReport: async (params?: Record<string, unknown>) => {
    const { data } = await apiClient.get('/showroom/reports/profit/', { params });
    return data;
  },

  getInventoryReport: async (params?: Record<string, unknown>) => {
    const { data } = await apiClient.get('/showroom/reports/inventory/', { params });
    return data;
  },

  getTopBrandsReport: async (params?: Record<string, unknown>) => {
    const { data } = await apiClient.get('/showroom/reports/top-brands/', { params });
    return data;
  },

  getCustomerInsightsReport: async (params?: Record<string, unknown>) => {
    const { data } = await apiClient.get('/showroom/reports/customer-insights/', { params });
    return data;
  },

  getRevenueTrendsReport: async (params?: Record<string, unknown>) => {
    const { data } = await apiClient.get('/showroom/reports/revenue-trends/', { params });
    return data;
  },

  getExportUrl: (params?: { format?: 'csv' | 'excel' | 'xlsx'; showroom_id?: number }) => {
    const rawBase = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
    const qp = new URLSearchParams();
    if (params?.format) qp.set('format', params.format);
    if (params?.showroom_id) qp.set('showroom_id', String(params.showroom_id));
    const query = qp.toString();
    return `${rawBase}/api/showroom/reports/export/${query ? `?${query}` : ''}`;
  },
};
